import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './stores/authStore';
import { ThemeProvider } from './contexts/ThemeContext';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import TicketsPage from './pages/TicketsPage';
import CreateTicketPage from './pages/CreateTicketPage';
import TicketDetailPage from './pages/TicketDetailPage';
import WorkspacePage from './pages/WorkspacePage';
import UsersPage from './pages/UsersPage';
import StatsPage from './pages/StatsPage';
import ClientsPage from './pages/ClientsPage';
import ClientDetailPage from './pages/ClientDetailPage';
import ClubChatPage from './pages/ClubChatPage';
import BroadcastPage from './pages/BroadcastPage';
import FinancePage from './pages/FinancePage';
import SettingsPage from './pages/SettingsPage';
import ShiftManagementPage from './pages/ShiftManagementPage';
import ShiftMonitoringPage from './pages/ShiftMonitoringPage';
import SLASettingsPage from './pages/SLASettingsPage';
import SecondLinePage from './pages/SecondLinePage';
import LeaderboardPage from './pages/LeaderboardPage';
import OperatorReportsPage from './pages/OperatorReportsPage';
import KnowledgeBasePage from './pages/KnowledgeBasePage';
import KBArticlePage from './pages/KBArticlePage';
import KBArticleEditPage from './pages/KBArticleEditPage';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import PWAUpdatePrompt from './components/PWAUpdatePrompt';


function App() {
  const { isAuthenticated } = useAuthStore();

  return (
    <ErrorBoundary>
    <ThemeProvider>
      <div className="App">
        {/* PWA: install prompt + "new version available" toast */}
        <PWAInstallPrompt />
        <PWAUpdatePrompt />
        <Routes>
        <Route 
          path="/login" 
          element={
            isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage />
          } 
        />
        <Route 
          path="/register" 
          element={
            isAuthenticated ? <Navigate to="/dashboard" replace /> : <RegisterPage />
          } 
        />
        <Route 
          path="/" 
          element={
            isAuthenticated ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />
          } 
        />
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute>
              <Layout>
                <DashboardPage />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/tickets" 
          element={
            <ProtectedRoute>
              <Layout>
                <TicketsPage />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/tickets/new" 
          element={
            <ProtectedRoute>
              <Layout>
                <CreateTicketPage />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route
          path="/tickets/:id"
          element={
            <ProtectedRoute>
              <Layout>
                <TicketDetailPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        {/* New Workspace (left-rail messenger view) — additive, opt-in.
            /work       — rail + empty state
            /work/:id   — rail + selected ticket (reuses TicketDetailPage) */}
        <Route
          path="/work"
          element={
            <ProtectedRoute>
              <Layout>
                <WorkspacePage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/work/:id"
          element={
            <ProtectedRoute>
              <Layout>
                <WorkspacePage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route 
          path="/users" 
          element={
            <ProtectedRoute requiredRoles={['admin', 'superadmin']}>
              <Layout>
                <UsersPage />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/stats" 
          element={
            <ProtectedRoute>
              <Layout>
                <StatsPage />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/clients" 
          element={
            <ProtectedRoute>
              <Layout>
                <ClientsPage />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/clients/:chatId" 
          element={
            <ProtectedRoute>
              <Layout>
                <ClientDetailPage />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route
          path="/clients/:chatId/chat"
          element={
            <ProtectedRoute>
              <Layout>
                <ClubChatPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route 
          path="/settings" 
          element={
            <ProtectedRoute>
              <Layout>
                <SettingsPage />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/shifts" 
          element={
            <ProtectedRoute requiredRoles={['operator', 'admin', 'superadmin']}>
              <Layout>
                <ShiftManagementPage />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/shifts/monitoring" 
          element={
            <ProtectedRoute requiredRoles={['admin', 'superadmin']}>
              <Layout>
                <ShiftMonitoringPage />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/sla-settings" 
          element={
            <ProtectedRoute requiredRoles={['superadmin']}>
              <Layout>
                <SLASettingsPage />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route
          path="/broadcasts"
          element={
            <ProtectedRoute>
              <Layout>
                <BroadcastPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route 
          path="/second-line" 
          element={
            <ProtectedRoute>
              <Layout>
                <SecondLinePage />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/leaderboard" 
          element={
            <ProtectedRoute>
              <Layout>
                <LeaderboardPage />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route
          path="/reports"
          element={
            <ProtectedRoute requiredRoles={['admin', 'superadmin']}>
              <Layout>
                <OperatorReportsPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/finance"
          element={
            <ProtectedRoute requiredRoles={['admin', 'superadmin']}>
              <Layout>
                <FinancePage />
              </Layout>
            </ProtectedRoute>
          }
        />
        {/* Knowledge Base — internal-only.
            /kb         — list & search
            /kb/new     — create (admin/superadmin only — gated inside the page)
            /kb/:id/edit— edit
            /kb/:idOrSlug — read */}
        <Route
          path="/kb"
          element={
            <ProtectedRoute>
              <Layout>
                <KnowledgeBasePage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/kb/new"
          element={
            <ProtectedRoute requiredRoles={['admin', 'superadmin']}>
              <Layout>
                <KBArticleEditPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/kb/:id/edit"
          element={
            <ProtectedRoute requiredRoles={['admin', 'superadmin']}>
              <Layout>
                <KBArticleEditPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/kb/:idOrSlug"
          element={
            <ProtectedRoute>
              <Layout>
                <KBArticlePage />
              </Layout>
            </ProtectedRoute>
          }
        />
      </Routes>
      </div>
    </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App; 